import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { PhoneIcon, XIcon } from 'lucide-react';
import { config, dealerContact } from '@/state/config.js';

/* Promotes the overlay into the top layer, for the reason in fullscreen-layer.css.
   Off under the dev server, where it would cover the harness's floating controls —
   so testing this path means serving a build, not `just dev`. */
const USE_TOP_LAYER = !__SM_DEV__;

interface FullscreenLayerProps {
  children: ComponentChildren;
  onClose?: () => void;
  /** Sits left of the call/close buttons. Absent on the boot-error chrome, which has no session. */
  headerAction?: ComponentChildren;
}

export function FullscreenLayer({ children, onClose, headerAction }: FullscreenLayerProps) {
  // Config may be absent here (a boot failure renders this chrome before config
  // loads), so fall back to a generic identity rather than an empty name + bullet.
  const accountName = config.value?.accountName.trim() ?? '';
  const displayName = accountName || 'Support';
  const { phone } = dealerContact.value;
  const avatarChar = displayName.charAt(0).toUpperCase();

  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    // Without popover support there are no `[popover]` UA styles either, so
    // bailing leaves the plain fixed overlay behind.
    if (!USE_TOP_LAYER || !el || typeof el.showPopover !== 'function') return;
    el.showPopover();
    return () => {
      try {
        el.hidePopover();
      } catch {
        // Already detached; the browser drops it from the top layer anyway.
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      popover={USE_TOP_LAYER ? 'manual' : undefined}
      className="sm-stack sm-fullscreen"
    >
      <div className="sm-stack sm-fullscreen-header">
        <div className="sm-fullscreen-header-inner">
          <div className="sm-type-heading sm-center sm-chat-header-avatar" aria-hidden="true">
            {avatarChar}
          </div>
          <div className="sm-stack sm-pair sm-chat-header-text">
            <div className="sm-type-body-strong sm-chat-header-name">{displayName}</div>
            <div className="sm-type-caption sm-chat-header-status">Online</div>
          </div>
          {headerAction}
          {phone ? (
            <a
              className="sm-center sm-chat-header-icon-btn"
              href={`tel:${phone}`}
              aria-label={`Call ${accountName || 'dealer'}`}
            >
              <PhoneIcon size={18} aria-hidden="true" />
            </a>
          ) : null}
          {onClose ? (
            <button
              type="button"
              className="sm-center sm-chat-header-icon-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <XIcon size={18} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
      <div className="sm-stack sm-fullscreen-stage">{children}</div>
    </div>
  );
}
